import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '../utils/api';
import { useAuth } from '../contexts/TestAuthContext';
import AppLayout from '../components/AppLayout';
import NowLineHeader, { NowLineStats } from '../components/NowLineHeader';
import {
  Lock,
  Unlock,
  User,
  RefreshCw,
  CheckCircle,
  AlertTriangle,
  ChevronRight,
  PanelRightOpen,
  PanelRightClose,
  Activity,
  TrendingUp,
  BarChart3,
  AlertCircle,
  FileText,
  FileSpreadsheet,
  BookOpen,
  FolderOpen,
  Search,
  X
} from 'lucide-react';

// Phase 3.2 Dashboard Components
import ForensicInsightsCard from '../components/dashboard/ForensicInsightsCard';
import InsuranceQuestionsCard from '../components/dashboard/InsuranceQuestionsCard';
import NextActionsChecklist from '../components/dashboard/NextActionsChecklist';
import DataHealthWidget from '../components/dashboard/DataHealthWidget';
import PendingAISuggestionsWidget from '../components/dashboard/PendingAISuggestionsWidget';
import TopPrioritiesCard from '../components/dashboard/TopPrioritiesCard';
import StressTestSummary from '../components/dashboard/StressTestSummary';
import IndustryBenchmark from '../components/IndustryBenchmark';

// Dashboard overview types
interface PipelineBreakdown {
  stage: string;
  count: number;
  value: number;
}

interface RecentActivityItem {
  action: string;
  description: string;
  report_id: string | null;
  business_name: string | null;
  timestamp: string | null;
}

interface DashboardOverview {
  pipeline: PipelineBreakdown[];
  recent_activity: RecentActivityItem[];
  total_portfolio_value: number;
  avg_case_strength: number;
  cases_ready_for_signoff: number;
  cases_stale_7_days: number;
}

interface CaseSummary {
  id: string;
  business_name: string;
  client_name: string | null;
  claimant_name: string | null;
  law_firm: string | null;
  event_type: string | null;
  event_date: string | null;
  business_type: string | null;
  assigned_queue: string;
  queue_reason: string;
  pipeline_stage: string;
  workflow_status: string | null;
  estimated_value: number;
  case_strength: number;
  transactions_need_review: number;
  completion_percentage: number;
  days_stale: number;
  last_updated: string | null;
  expected_ready_date: string | null;
  primary_action: string;
  primary_action_url: string;
  // Claim fields
  is_claimed?: boolean;
  claimed_by_id?: string | null;
  claimed_by_name?: string | null;
  claimed_at?: string | null;
  // Readiness fields
  is_ready_for_review?: boolean;
  readiness_issues?: string[];
  // AI-generated next action from roadmap
  next_action?: string | null;
  next_action_impact?: string | null;
  pending_classifications?: number;
  // Quick-action URLs
  wizard_url?: string | null;
  reconciliation_url?: string | null;
  narrator_url?: string | null;
  report_url?: string | null;
  documents_url?: string | null;
}

interface QueueStats {
  total_cases: number;
  needs_attention: number;
  no_action_needed: number;
  data_specialist: number;
  accountant: number;
  ai_processing: number;
  ready: number;
  client: number;
  attorney: number;
  total_value: number;
}

interface QueueResponse {
  cases: CaseSummary[];
  total_count: number;
  page: number;
  page_size: number;
  total_pages: number;
  available_count?: number;
  claimed_by_me_count?: number;
}

// Status badge helper for accountant view
const getStatusBadge = (caseItem: CaseSummary) => {
  const { assigned_queue, days_stale, case_strength } = caseItem;
  
  if (days_stale >= 5) {
    return { label: 'stale', color: 'bg-red-100 text-red-700' };
  }
  if (assigned_queue === 'ready' || case_strength >= 80) {
    return { label: 'ready', color: 'bg-green-100 text-green-700' };
  }
  if (case_strength >= 60) {
    return { label: 'strong', color: 'bg-blue-100 text-blue-700' };
  }
  if (case_strength >= 40) {
    return { label: 'review', color: 'bg-yellow-100 text-yellow-700' };
  }
  return { label: 'weak', color: 'bg-red-100 text-red-700' };
};

// Action text helper
const getActionText = (caseItem: CaseSummary) => {
  const { case_strength, queue_reason, assigned_queue } = caseItem;
  
  if (assigned_queue === 'ready') {
    return 'Ready for sign-off';
  }
  if (case_strength < 40) {
    return 'Needs strengthening';
  }
  if (case_strength < 60) {
    return 'Review calculations';
  }
  return queue_reason || 'Review and sign off';
};

// Sign-off ceremony toast component
const SignOffToast: React.FC<{
  show: boolean;
  businessName: string;
  onClose: () => void;
}> = ({ show, businessName, onClose }) => {
  useEffect(() => {
    if (show) {
      const timer = setTimeout(onClose, 4000);
      return () => clearTimeout(timer);
    }
  }, [show, onClose]);

  if (!show) return null;

  return (
    <div className="fixed bottom-6 right-6 z-50 animate-bounce-once">
      <div className="bg-green-600 text-white px-6 py-4 rounded-xl shadow-2xl flex items-center gap-4 max-w-md">
        <div className="text-4xl">🎉</div>
        <div>
          <div className="font-semibold text-lg">Case Signed Off!</div>
          <div className="text-green-100 text-sm">{businessName} is ready for delivery</div>
        </div>
        <button onClick={onClose} className="ml-2 text-green-200 hover:text-white" aria-label="Dismiss notification">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    </div>
  );
};

const AccountantQueue: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [stats, setStats] = useState<QueueStats | null>(null);
  const [cases, setCases] = useState<CaseSummary[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [availableCount, setAvailableCount] = useState(0);
  const [myClaimsCount, setMyClaimsCount] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Phase 3.2: Sidebar state and selected case
  const [showSidebar, setShowSidebar] = useState(true);
  const [selectedCaseId, setSelectedCaseId] = useState<string | null>(null);
  
  // Tabs: 'available' or 'my-cases'
  const [activeTab, setActiveTab] = useState<'available' | 'my-cases'>('available');

  // Search & filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  
  // Claim action state
  const [claimingCase, setClaimingCase] = useState<string | null>(null);
  const [releasingCase, setReleasingCase] = useState<string | null>(null);
  
  // Sign-off ceremony state
  const [showSignOffToast, setShowSignOffToast] = useState(false);
  const [signedOffBusiness, setSignedOffBusiness] = useState('');
  const [signingOff, setSigningOff] = useState<string | null>(null);
  
  // Now-Line stats for Phase A UX
  const [nowLineStats, setNowLineStats] = useState<NowLineStats>({
    needsAttention: { count: 0, breakdown: [] },
    noActionNeeded: { count: 0, breakdown: [] }
  });
  const [nowLineLoading, setNowLineLoading] = useState(true);
  
  // Dashboard overview for sidebar
  const [overview, setOverview] = useState<DashboardOverview | null>(null);

  // Financial metrics for IndustryBenchmark
  const [caseMetrics, setCaseMetrics] = useState<{cogsRatio?: number; grossMargin?: number; operatingExpenseRatio?: number; netProfitMargin?: number} | null>(null);
  
  // Current user info for claims
  const currentAccountantId = user?.sub || user?.userId || user?.id || 'current-user';
  const currentAccountantName = user?.name || user?.fullName || user?.email || 'Accountant';
  const currentAccountantEmail = user?.email;

  useEffect(() => {
    fetchData();
    fetchNowLineStats();
    fetchDashboardOverview();
  }, [page, activeTab]);

  // Fetch financial metrics when a case is selected (for IndustryBenchmark)
  useEffect(() => {
    if (!selectedCaseId) {
      setCaseMetrics(null);
      return;
    }
    (async () => {
      try {
        const data = await api.get(`/api/v1/reconciliation/pl-grid/${selectedCaseId}`);
        const grid = data?.pl_grid || data?.grid_by_year || {};
        // Sum up income and COGS across most recent year
        const years = Object.keys(grid).sort();
        const latestYear = years[years.length - 1];
        if (!latestYear || !grid[latestYear]) return;

        const yearData = grid[latestYear];
        const sumSection = (section: string) => {
          const sectionData = yearData[section] || {};
          return Object.values(sectionData).reduce((acc: number, months: any) => {
            if (Array.isArray(months)) return acc + months.reduce((s: number, v: number) => s + (v || 0), 0);
            return acc;
          }, 0);
        };
        const income = sumSection('INCOME');
        const cogs = sumSection('COST OF GOODS SOLD');
        const expenses = sumSection('OPERATING EXPENSES');
        if (income > 0) {
          setCaseMetrics({
            cogsRatio: Math.round((cogs / income) * 100),
            grossMargin: Math.round(((income - cogs) / income) * 100),
            operatingExpenseRatio: Math.round((expenses / income) * 100),
            netProfitMargin: Math.round(((income - cogs - expenses) / income) * 100)
          });
        }
      } catch {
        // P&L grid may not be available for all cases
        setCaseMetrics(null);
      }
    })();
  }, [selectedCaseId]);

  const fetchNowLineStats = async () => {
    setNowLineLoading(true);
    try {
      const data = await api.get<{needs_attention: any; no_action_needed: any}>(
        '/api/v1/queue/nowline-stats?role=accountant'
      );
      setNowLineStats({
        needsAttention: {
          count: data.needs_attention?.count || 0,
          breakdown: data.needs_attention?.breakdown || []
        },
        noActionNeeded: {
          count: data.no_action_needed?.count || 0,
          breakdown: data.no_action_needed?.breakdown || []
        }
      });
    } catch (error) {
      console.debug('Now-Line stats not available:', error);
    } finally {
      setNowLineLoading(false);
    }
  };

  const fetchDashboardOverview = async () => {
    try {
      const data = await api.get<DashboardOverview>('/api/v1/queue/accountant/dashboard-overview');
      setOverview(data);
    } catch (error) {
      console.debug('Dashboard overview not available:', error);
    }
  };


  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch stats
      const statsData = await api.get<QueueStats>('/api/v1/queue/stats?user_role=accountant');
      setStats(statsData);

      // Fetch queue based on active tab
      let queueData: QueueResponse;
      if (activeTab === 'my-cases') {
        queueData = await api.get<QueueResponse>(
          `/api/v1/queue/accountant/my-cases?accountant_id=${encodeURIComponent(currentAccountantId)}&page=${page}&page_size=20`
        );
      } else {
        queueData = await api.get<QueueResponse>(
          `/api/v1/queue/accountant/available?accountant_id=${encodeURIComponent(currentAccountantId)}&page=${page}&page_size=20`
        );
      }
      
      setCases(queueData.cases);
      setTotalCount(queueData.total_count);
      setTotalPages(queueData.total_pages);
      setAvailableCount(queueData.available_count || 0);
      setMyClaimsCount(queueData.claimed_by_me_count || 0);
      setError(null);
    } catch (err: any) {
      // Fallback to old endpoint if new ones not available
      try {
        const queueData = await api.get<QueueResponse>(`/api/v1/queue/accountant?page=${page}&page_size=20`);
        setCases(queueData.cases);
        setTotalCount(queueData.total_count);
        setTotalPages(queueData.total_pages);
        setError(null);
      } catch (fallbackErr: any) {
        setError(fallbackErr.message || 'Failed to load queue');
      }
    } finally {
      setLoading(false);
    }
  };

  // Claim a case
  const handleClaim = async (e: React.MouseEvent, caseItem: CaseSummary) => {
    e.stopPropagation();
    setClaimingCase(caseItem.id);
    try {
      await api.post(
        `/api/v1/queue/accountant/claim/${caseItem.id}?accountant_id=${encodeURIComponent(currentAccountantId)}&accountant_name=${encodeURIComponent(currentAccountantName)}${currentAccountantEmail ? `&accountant_email=${encodeURIComponent(currentAccountantEmail)}` : ''}`,
        {}
      );
      await fetchData();
    } catch (err: any) {
      setError(err.message || 'Failed to claim case');
    } finally {
      setClaimingCase(null);
    }
  };

  // Release a case
  const handleRelease = async (e: React.MouseEvent, caseItem: CaseSummary) => {
    e.stopPropagation();
    setReleasingCase(caseItem.id);
    try {
      await api.post(
        `/api/v1/queue/accountant/release/${caseItem.id}?accountant_id=${encodeURIComponent(currentAccountantId)}`,
        {}
      );
      await fetchData();
    } catch (err: any) {
      setError(err.message || 'Failed to release case');
    } finally {
      setReleasingCase(null);
    }
  };

  // Sign-off handler with ceremony
  const handleSignOff = async (e: React.MouseEvent, caseItem: CaseSummary) => {
    e.stopPropagation(); // Prevent row click
    setSigningOff(caseItem.id);
    try {
      // Call the real sign-off endpoint
      const response = await api.post(
        `/api/v1/case-signoff/${caseItem.id}?accountant_id=${encodeURIComponent(currentAccountantId)}&accountant_name=${encodeURIComponent(currentAccountantName)}`,
        { notes: '', override_warnings: false }
      );
      
      if (response.success) {
        // Show celebration toast
        setSignedOffBusiness(caseItem.business_name || 'Case');
        setShowSignOffToast(true);
        
        // Refresh data after sign-off
        await fetchData();
      } else {
        // If blocked, show the blocking issues
        const issues = response.blocking_issues?.join(', ') || response.message;
        setError(`Cannot sign off: ${issues}`);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to sign off');
    } finally {
      setSigningOff(null);
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  // Phase 3.2: Handle case selection for sidebar (without navigation)
  const handleCaseSelect = (caseItem: CaseSummary) => {
    setSelectedCaseId(caseItem.id === selectedCaseId ? null : caseItem.id);
  };

  // Get selected case data for sidebar widgets
  const selectedCase = cases.find(c => c.id === selectedCaseId);

  // Filter cases by search and status
  const filteredCases = cases.filter(c => {
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      const matchesSearch =
        (c.business_name || '').toLowerCase().includes(q) ||
        (c.client_name || '').toLowerCase().includes(q) ||
        (c.law_firm || '').toLowerCase().includes(q) ||
        (c.event_type || '').toLowerCase().includes(q);
      if (!matchesSearch) return false;
    }
    if (statusFilter !== 'all') {
      if (statusFilter === 'ready' && c.assigned_queue !== 'ready' && c.case_strength < 70) return false;
      if (statusFilter === 'stale' && c.days_stale < 5) return false;
      if (statusFilter === 'needs-review' && c.transactions_need_review === 0 && c.completion_percentage >= 90) return false;
    }
    return true;
  });

  if (loading && !stats) {
    return (
      <AppLayout>
        <div className="min-h-screen bg-slate-50 flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="min-h-screen bg-slate-50">
        {/* Sign Off Celebration Toast */}
        <SignOffToast
          show={showSignOffToast}
          businessName={signedOffBusiness}
          onClose={() => setShowSignOffToast(false)}
        />
        
        {/* Header */}
        <div className="bg-white border-b border-slate-200 px-6 py-4">
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-semibold text-slate-900">Accountant Queue</h1>
              <p className="text-slate-600 mt-1">Review calculations and sign off on cases</p>
            </div>
            <div className="flex items-center gap-4">
              <button
                onClick={() => setShowSidebar(!showSidebar)}
                className="px-3 py-2 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 flex items-center gap-2 text-sm text-slate-600"
                title={showSidebar ? 'Hide insights panel' : 'Show insights panel'}
                aria-label={showSidebar ? 'Hide insights panel' : 'Show insights panel'}
                aria-expanded={showSidebar}
                data-testid="toggle-sidebar-btn"
              >
                {showSidebar ? (
                  <>
                    <PanelRightClose className="w-4 h-4" aria-hidden="true" />
                    <span>Hide Panel</span>
                  </>
                ) : (
                  <>
                    <PanelRightOpen className="w-4 h-4" aria-hidden="true" />
                    <span>Show Panel</span>
                  </>
                )}
              </button>
              <button
                onClick={fetchData}
                disabled={loading}
                className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1"
                aria-label="Refresh queue data"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} aria-hidden="true" />
                Refresh
              </button>
              <Link
                to="/dashboard"
                className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1"
              >
                ← Back to Dashboard
              </Link>
            </div>
          </div>
        </div>

      <div className="px-6 py-8">
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 flex items-center gap-2 max-w-7xl mx-auto">
            <AlertTriangle className="w-5 h-5" />
            {error}
            <button onClick={() => setError(null)} className="ml-auto text-red-500 hover:text-red-700" aria-label="Dismiss error">✕</button>
          </div>
        )}

        {/* Now-Line Header - Phase A UX Enhancement */}
        <div className="max-w-7xl mx-auto mb-6">
          <NowLineHeader
            stats={nowLineStats}
            userName={currentAccountantName}
            loading={nowLineLoading}
            onViewNeedsAttention={() => setActiveTab('available')}
            onViewNoAction={() => setActiveTab('my-cases')}
          />
        </div>

        {/* Main content with optional sidebar */}
        <div className="max-w-7xl mx-auto">
          <div className={`flex gap-6`}>
            {/* Main Queue Section */}
            <div className={`flex-1 min-w-0 ${showSidebar ? '' : ''}`}>
              {/* Summary Cards - Responsive */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 mb-8" data-testid="stats-cards">
                <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="stat-available">
                  <div className="flex items-center gap-3 mb-1">
                    <div className="w-2.5 h-2.5 rounded-full bg-blue-500"></div>
                    <span className="text-sm font-medium text-slate-600 uppercase tracking-wide">
                      Available
                    </span>
                  </div>
                  <div className="text-3xl font-bold text-slate-900">
                    {availableCount || stats?.accountant || 0}
                  </div>
                  <p className="text-slate-500 text-sm mt-1">unclaimed cases</p>
                </div>

                <div className="bg-white rounded-xl border border-slate-200 p-5">
                  <div className="flex items-center gap-3 mb-1">
                    <div className="w-2.5 h-2.5 rounded-full bg-amber-500"></div>
                    <span className="text-sm font-medium text-slate-600 uppercase tracking-wide">
                      My Claims
                    </span>
                  </div>
                  <div className="text-3xl font-bold text-slate-900">
                    {myClaimsCount}
                  </div>
                  <p className="text-slate-500 text-sm mt-1">cases I'm working on</p>
                </div>

                <div className="bg-white rounded-xl border border-slate-200 p-5">
                  <div className="flex items-center gap-3 mb-1">
                    <div className="w-2.5 h-2.5 rounded-full bg-green-500"></div>
                    <span className="text-sm font-medium text-slate-600 uppercase tracking-wide">
                      Ready
                    </span>
                  </div>
                  <div className="text-3xl font-bold text-slate-900">
                    {stats?.ready || 0}
                  </div>
                  <p className="text-slate-500 text-sm mt-1">ready for delivery</p>
                </div>

                <div className="bg-white rounded-xl border border-slate-200 p-5">
                  <div className="flex items-center gap-3 mb-1">
                    <div className="w-2.5 h-2.5 rounded-full bg-purple-500"></div>
                    <span className="text-sm font-medium text-slate-600 uppercase tracking-wide">
                      Portfolio Value
                    </span>
                  </div>
                  <div className="text-3xl font-bold text-slate-900">
                    {formatCurrency(stats?.total_value || 0)}
                  </div>
                  <p className="text-slate-500 text-sm mt-1">total estimated claims</p>
                </div>
              </div>

              {/* Tabs - ARIA accessible */}
              <div 
                className="flex items-center gap-4 mb-6 border-b border-slate-200"
                role="tablist"
                aria-label="Case queue tabs"
              >
                <button
                  onClick={() => { setActiveTab('available'); setPage(1); }}
                  role="tab"
                  aria-selected={activeTab === 'available'}
                  aria-controls="tabpanel-available"
                  id="tab-available"
                  data-testid="tab-available"
                  tabIndex={activeTab === 'available' ? 0 : -1}
                  className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors min-h-[44px] ${
                    activeTab === 'available'
                      ? 'border-blue-600 text-blue-600'
                      : 'border-transparent text-slate-600 hover:text-slate-900'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <Unlock className="w-4 h-4" aria-hidden="true" />
                    Available Cases
                    <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full text-xs" aria-label={`${availableCount || stats?.accountant || 0} available`}>
                      {availableCount || stats?.accountant || 0}
                    </span>
                  </div>
                </button>
                <button
                  onClick={() => { setActiveTab('my-cases'); setPage(1); }}
                  role="tab"
                  aria-selected={activeTab === 'my-cases'}
                  aria-controls="tabpanel-my-cases"
                  id="tab-my-cases"
                  data-testid="tab-my-cases"
                  tabIndex={activeTab === 'my-cases' ? 0 : -1}
                  className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors min-h-[44px] ${
                    activeTab === 'my-cases'
                      ? 'border-blue-600 text-blue-600'
                      : 'border-transparent text-slate-600 hover:text-slate-900'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <User className="w-4 h-4" aria-hidden="true" />
                    My Cases
                    <span className="px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full text-xs" aria-label={`${myClaimsCount} claimed`}>
                      {myClaimsCount}
                    </span>
                  </div>
                </button>
              </div>

              {/* Queue Header with Search & Filters */}
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-4">
                <h2 className="text-lg font-semibold text-slate-900" id="queue-heading" data-testid="queue-heading">
                  {activeTab === 'available' ? 'Available Cases' : 'My Claimed Cases'} ({filteredCases.length}{filteredCases.length !== totalCount ? ` of ${totalCount}` : ''})
                </h2>
                <div className="flex items-center gap-2 w-full sm:w-auto">
                  <div className="relative flex-1 sm:flex-none sm:w-56">
                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <input
                      type="text"
                      placeholder="Search cases..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-full pl-8 pr-8 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      data-testid="search-input"
                      aria-label="Search cases by name, client, law firm, or event type"
                    />
                    {searchQuery && (
                      <button
                        onClick={() => setSearchQuery('')}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                        aria-label="Clear search"
                      >
                        <X className="w-4 h-4" aria-hidden="true" />
                      </button>
                    )}
                  </div>
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                    data-testid="status-filter"
                    aria-label="Filter by case status"
                  >
                    <option value="all">All Status</option>
                    <option value="ready">Ready for Sign-off</option>
                    <option value="needs-review">Needs Review</option>
                    <option value="stale">Stale (5+ days)</option>
                  </select>
                </div>
              </div>

              {/* Case List - ARIA accessible */}
              <div 
                className="bg-white rounded-xl border border-slate-200 overflow-hidden"
                role="tabpanel"
                id={`tabpanel-${activeTab}`}
                aria-labelledby={`tab-${activeTab}`}
              >
                {filteredCases.length === 0 ? (
                  <div className="p-8 text-center text-slate-500">
                    {searchQuery || statusFilter !== 'all'
                      ? 'No cases match your search criteria. Try adjusting your filters.'
                      : activeTab === 'available'
                        ? 'No available cases to claim. All cases are being worked on!'
                        : 'You haven\'t claimed any cases yet. Go to Available Cases to claim one.'}
                  </div>
                ) : (
                  <>
                    {/* Table Header - Hidden on mobile */}
                    <div className="hidden md:grid grid-cols-12 gap-4 px-4 py-3 bg-slate-50 border-b border-slate-200 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                      <div className="col-span-3">Case</div>
                      <div className="col-span-2">Event / Date</div>
                      <div className="col-span-3">Status</div>
                      <div className="col-span-1 text-center">Strength</div>
                      <div className="col-span-1 text-center">Value</div>
                      <div className="col-span-2 text-right">Quick Actions</div>
                    </div>

                    {/* Table Body */}
                    <div className="divide-y divide-slate-100 max-h-[500px] overflow-y-auto">
                      {filteredCases.map((caseItem) => {
                        const canSignOff = caseItem.case_strength >= 70 || caseItem.assigned_queue === 'ready';
                        const isMyCase = caseItem.claimed_by_id === currentAccountantId;
                        const isClaimed = caseItem.is_claimed || caseItem.claimed_by_id;
                        
                        // Determine status for the colored dot
                        let statusDot = 'bg-gray-400';
                        let statusTitle = 'Unknown';
                        if (caseItem.assigned_queue === 'ready' || canSignOff) {
                          statusDot = 'bg-green-500';
                          statusTitle = 'Ready for sign-off';
                        } else if (caseItem.case_strength >= 50) {
                          statusDot = 'bg-blue-500';
                          statusTitle = 'Strong case';
                        } else if (caseItem.case_strength >= 30) {
                          statusDot = 'bg-amber-500';
                          statusTitle = 'Needs review';
                        } else {
                          statusDot = 'bg-red-500';
                          statusTitle = 'Weak - needs attention';
                        }
                        
                        // Action needed text
                        let actionText = caseItem.next_action || '';
                        if (caseItem.next_action_impact) {
                          actionText += ` (${caseItem.next_action_impact})`;
                        }
                        if (!actionText) {
                          if (caseItem.readiness_issues && caseItem.readiness_issues.length > 0) {
                            actionText = caseItem.readiness_issues[0];
                          } else if (canSignOff) {
                            actionText = 'Ready for accountant sign-off';
                          } else {
                            actionText = 'Awaiting AI analysis';
                          }
                        }
                        
                        const progressPct = caseItem.case_strength || 0;
                        const getProgressColor = (pct: number) => {
                          if (pct >= 80) return 'bg-green-500';
                          if (pct >= 50) return 'bg-blue-500';
                          if (pct >= 25) return 'bg-amber-500';
                          return 'bg-red-500';
                        };
                        
                        // Format event type
                        const eventLabel = caseItem.event_type
                          ? caseItem.event_type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
                          : null;
                        
                        // Display name (prefer client_name, fallback to business_name)
                        const displayName = caseItem.business_name || 'Unknown';
                        const subName = caseItem.client_name && caseItem.client_name !== caseItem.business_name 
                          ? caseItem.client_name : null;
                        
                        const handleKeyDown = (e: React.KeyboardEvent) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            handleCaseSelect(caseItem);
                          }
                        };
                        
                        return (
                          <div
                            key={caseItem.id}
                            role="button"
                            tabIndex={0}
                            data-testid={`case-row-${caseItem.id}`}
                            aria-label={`${displayName}, ${statusTitle}, ${progressPct}% strength`}
                            onKeyDown={handleKeyDown}
                            onClick={() => handleCaseSelect(caseItem)}
                            className={`
                              flex flex-col gap-2 p-4
                              md:grid md:grid-cols-12 md:gap-4 md:px-4 md:py-3 md:items-center
                              hover:bg-blue-50/50 cursor-pointer transition-colors
                              ${selectedCaseId === caseItem.id ? 'bg-blue-50 ring-1 ring-blue-200' : ''}
                            `}
                          >
                            {/* Mobile: Compact card layout */}
                            <div className="flex items-center gap-3 md:hidden">
                              <span className={`inline-block w-2.5 h-2.5 rounded-full shrink-0 ${statusDot}`} title={statusTitle} />
                              <div className="min-w-0 flex-1">
                                <div className="font-medium text-slate-900 truncate">{displayName}</div>
                                <div className="text-xs text-slate-500">{caseItem.law_firm || 'No law firm'}{eventLabel ? ` · ${eventLabel}` : ''}</div>
                              </div>
                              <div className="text-sm font-medium text-slate-600">{progressPct}%</div>
                            </div>
                            <div className="md:hidden text-sm text-slate-600 pl-5">{actionText}</div>

                            {/* Desktop: Case (col-span-3) */}
                            <div className="hidden md:flex col-span-3 items-center gap-2 min-w-0 overflow-hidden">
                              <span className={`inline-block w-2 h-2 rounded-full shrink-0 ${statusDot}`} title={statusTitle} />
                              <div className="min-w-0 flex-1 overflow-hidden">
                                <div className="font-medium text-slate-900 truncate text-sm leading-tight" data-testid={`case-name-${caseItem.id}`}>
                                  {displayName}
                                </div>
                                <div className="text-xs text-slate-500 truncate leading-tight mt-0.5">
                                  {caseItem.law_firm || 'No law firm'}
                                </div>
                                <div className="text-[10px] text-slate-400 font-mono leading-tight">#{caseItem.id.slice(0, 8).toUpperCase()}</div>
                              </div>
                            </div>

                            {/* Desktop: Event / Date (col-span-2) */}
                            <div className="hidden md:flex col-span-2 flex-col min-w-0 overflow-hidden gap-0.5">
                              {eventLabel ? (
                                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-slate-100 text-slate-600 rounded text-[11px] font-medium w-fit max-w-full" data-testid={`case-event-${caseItem.id}`}>
                                  <AlertCircle className="w-3 h-3 shrink-0 text-slate-400" />
                                  <span className="truncate">{eventLabel}</span>
                                </span>
                              ) : caseItem.business_type ? (
                                <span className="text-xs text-slate-500 truncate">{caseItem.business_type}</span>
                              ) : null}
                              {caseItem.event_date && (
                                <span className="text-[11px] text-slate-400">
                                  {new Date(caseItem.event_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit' })}
                                </span>
                              )}
                            </div>

                            {/* Desktop: Status (col-span-3) */}
                            <div className="hidden md:flex col-span-3 flex-col min-w-0 overflow-hidden">
                              <span className="text-slate-600 text-xs leading-snug line-clamp-2" title={actionText}>{actionText}</span>
                              {caseItem.pending_classifications ? (
                                <span className="inline-flex items-center gap-1 mt-0.5 text-[11px] text-amber-600">
                                  <AlertTriangle className="w-3 h-3 shrink-0" />
                                  {caseItem.pending_classifications} pending
                                </span>
                              ) : null}
                              {/* Checklist badges */}
                              <div className="flex items-center gap-1 mt-1 flex-wrap">
                                <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-medium ${
                                  caseItem.transactions_need_review === 0 ? 'bg-green-50 text-green-700' : 'bg-amber-50 text-amber-700'
                                }`}>
                                  {caseItem.transactions_need_review === 0 ? <CheckCircle className="w-2.5 h-2.5" /> : <AlertTriangle className="w-2.5 h-2.5" />}
                                  Data
                                </span>
                                <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-medium ${
                                  caseItem.completion_percentage >= 90 ? 'bg-green-50 text-green-700' : 'bg-amber-50 text-amber-700'
                                }`}>
                                  {caseItem.completion_percentage >= 90 ? <CheckCircle className="w-2.5 h-2.5" /> : <AlertTriangle className="w-2.5 h-2.5" />}
                                  Docs
                                </span>
                                <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-medium ${
                                  caseItem.is_ready_for_review ? 'bg-green-50 text-green-700' : 'bg-slate-50 text-slate-500'
                                }`}>
                                  {caseItem.is_ready_for_review ? <CheckCircle className="w-2.5 h-2.5" /> : <AlertTriangle className="w-2.5 h-2.5" />}
                                  Calc
                                </span>
                              </div>
                            </div>

                            {/* Desktop: Strength (col-span-1) */}
                            <div className="hidden md:flex col-span-1 items-center justify-center">
                              <div className="relative w-9 h-9">
                                <svg className="w-9 h-9 -rotate-90" viewBox="0 0 36 36">
                                  <circle cx="18" cy="18" r="14" fill="none" stroke="#e2e8f0" strokeWidth="3" />
                                  <circle cx="18" cy="18" r="14" fill="none" strokeWidth="3"
                                    className={progressPct >= 70 ? 'stroke-green-500' : progressPct >= 40 ? 'stroke-amber-500' : 'stroke-red-500'}
                                    strokeDasharray={`${(progressPct / 100) * 88} 88`}
                                    strokeLinecap="round"
                                  />
                                </svg>
                                <span className="absolute inset-0 flex items-center justify-center text-[10px] font-bold text-slate-700">{progressPct}</span>
                              </div>
                            </div>

                            {/* Desktop: Value (col-span-1) */}
                            <div className="hidden md:flex col-span-1 flex-col items-center min-w-0">
                              {caseItem.estimated_value > 0 ? (
                                <span className="text-xs font-semibold text-slate-700" data-testid={`case-value-${caseItem.id}`}>
                                  {caseItem.estimated_value >= 1000000
                                    ? `$${(caseItem.estimated_value / 1000000).toFixed(1)}M`
                                    : caseItem.estimated_value >= 1000
                                    ? `$${(caseItem.estimated_value / 1000).toFixed(0)}K`
                                    : `$${caseItem.estimated_value.toFixed(0)}`}
                                </span>
                              ) : (
                                <span className="text-[10px] text-slate-400">N/A</span>
                              )}
                              {caseItem.days_stale > 0 && (
                                <span className="text-[10px] text-slate-400">{caseItem.days_stale}d ago</span>
                              )}
                            </div>

                            {/* Desktop: Quick Actions (col-span-2) */}
                            <div className="hidden md:flex col-span-2 items-center justify-end gap-0.5">
                              {caseItem.wizard_url && (
                                <button
                                  onClick={(e) => { e.stopPropagation(); navigate(caseItem.wizard_url!); }}
                                  className="p-1.5 hover:bg-blue-50 rounded transition-colors group"
                                  title="Open in Wizard"
                                  aria-label={`Open ${displayName} in Wizard`}
                                  data-testid={`tool-wizard-${caseItem.id}`}
                                >
                                  <FileText className="w-4 h-4 text-slate-400 group-hover:text-blue-600" aria-hidden="true" />
                                </button>
                              )}
                              {caseItem.reconciliation_url && (
                                <button
                                  onClick={(e) => { e.stopPropagation(); navigate(caseItem.reconciliation_url!); }}
                                  className="p-1.5 hover:bg-green-50 rounded transition-colors group"
                                  title="P&L Reconciliation"
                                  aria-label={`P&L Reconciliation for ${displayName}`}
                                  data-testid={`tool-reconciliation-${caseItem.id}`}
                                >
                                  <FileSpreadsheet className="w-4 h-4 text-slate-400 group-hover:text-green-600" aria-hidden="true" />
                                </button>
                              )}
                              {caseItem.narrator_url && (
                                <button
                                  onClick={(e) => { e.stopPropagation(); navigate(caseItem.narrator_url!); }}
                                  className="p-1.5 hover:bg-purple-50 rounded transition-colors group"
                                  title="Generate Narrative"
                                  aria-label={`Generate Narrative for ${displayName}`}
                                  data-testid={`tool-narrator-${caseItem.id}`}
                                >
                                  <BookOpen className="w-4 h-4 text-slate-400 group-hover:text-purple-600" aria-hidden="true" />
                                </button>
                              )}
                              {caseItem.documents_url && (
                                <button
                                  onClick={(e) => { e.stopPropagation(); navigate(caseItem.documents_url!); }}
                                  className="p-1.5 hover:bg-amber-50 rounded transition-colors group"
                                  title="View Documents"
                                  aria-label={`View Documents for ${displayName}`}
                                  data-testid={`tool-documents-${caseItem.id}`}
                                >
                                  <FolderOpen className="w-4 h-4 text-slate-400 group-hover:text-amber-600" aria-hidden="true" />
                                </button>
                              )}
                              {activeTab === 'available' && !isClaimed && (
                                <button
                                  onClick={(e) => handleClaim(e, caseItem)}
                                  disabled={claimingCase === caseItem.id}
                                  className="ml-1 px-2 py-1 text-[11px] font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 rounded transition-colors flex items-center gap-1"
                                  title="Claim this case"
                                  data-testid={`tool-claim-${caseItem.id}`}
                                >
                                  <Lock className="w-3 h-3" />
                                  {claimingCase === caseItem.id ? 'Claiming...' : 'Claim'}
                                </button>
                              )}
                              {activeTab === 'my-cases' && isMyCase && !canSignOff && (
                                <button
                                  onClick={(e) => handleRelease(e, caseItem)}
                                  disabled={releasingCase === caseItem.id}
                                  className="ml-1 px-2 py-1 text-[11px] font-medium text-slate-600 bg-slate-100 hover:bg-slate-200 disabled:opacity-50 rounded transition-colors flex items-center gap-1"
                                  title="Release this case"
                                  data-testid={`tool-release-${caseItem.id}`}
                                >
                                  <Unlock className="w-3 h-3" />
                                  {releasingCase === caseItem.id ? 'Releasing...' : 'Release'}
                                </button>
                              )}
                              {activeTab === 'my-cases' && canSignOff && (
                                <button
                                  onClick={(e) => handleSignOff(e, caseItem)}
                                  disabled={signingOff === caseItem.id}
                                  className="ml-1 px-2 py-1 text-[11px] font-medium text-white bg-green-600 hover:bg-green-700 disabled:opacity-50 rounded transition-colors"
                                  title="Sign off"
                                  data-testid={`tool-signoff-${caseItem.id}`}
                                >
                                  {signingOff === caseItem.id ? 'Signing...' : 'Sign Off'}
                                </button>
                              )}
                              <button
                                onClick={(e) => { e.stopPropagation(); navigate(caseItem.primary_action_url); }}
                                className="p-1 hover:bg-slate-100 rounded ml-0.5"
                                title="Open case"
                                aria-label={`Open case ${displayName}`}
                              >
                                <ChevronRight className="w-4 h-4 text-slate-400" aria-hidden="true" />
                              </button>
                            </div>

                            {/* Mobile: Action buttons */}
                            <div className="flex items-center gap-2 md:hidden pl-5 mt-1">
                              {caseItem.wizard_url && (
                                <button onClick={(e) => { e.stopPropagation(); navigate(caseItem.wizard_url!); }}
                                  className="px-2 py-1 text-xs text-blue-600 bg-blue-50 rounded">Wizard</button>
                              )}
                              {caseItem.reconciliation_url && (
                                <button onClick={(e) => { e.stopPropagation(); navigate(caseItem.reconciliation_url!); }}
                                  className="px-2 py-1 text-xs text-green-600 bg-green-50 rounded">P&L</button>
                              )}
                              {activeTab === 'available' && !isClaimed && (
                                <button
                                  onClick={(e) => handleClaim(e, caseItem)}
                                  disabled={claimingCase === caseItem.id}
                                  className="px-2 py-1 text-xs font-medium text-white bg-blue-600 rounded disabled:opacity-50 min-h-[44px]"
                                >
                                  {claimingCase === caseItem.id ? 'Claiming...' : 'Claim'}
                                </button>
                              )}
                              {activeTab === 'my-cases' && isMyCase && !canSignOff && (
                                <button
                                  onClick={(e) => handleRelease(e, caseItem)}
                                  disabled={releasingCase === caseItem.id}
                                  className="px-2 py-1 text-xs font-medium text-slate-600 bg-slate-100 rounded disabled:opacity-50 min-h-[44px]"
                                >
                                  {releasingCase === caseItem.id ? 'Releasing...' : 'Release'}
                                </button>
                              )}
                              {activeTab === 'my-cases' && canSignOff && (
                                <button
                                  onClick={(e) => handleSignOff(e, caseItem)}
                                  disabled={signingOff === caseItem.id}
                                  className="px-2 py-1 text-xs font-medium text-white bg-green-600 rounded disabled:opacity-50 min-h-[44px]"
                                >
                                  {signingOff === caseItem.id ? 'Signing...' : 'Sign Off'}
                                </button>
                              )}
                              <button onClick={(e) => { e.stopPropagation(); navigate(caseItem.primary_action_url); }}
                                className="ml-auto px-3 py-1.5 text-xs font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-lg min-h-[44px]">
                                View →
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </>
                )}

          {/* Pagination */}
          {totalPages > 1 && (
            <nav className="px-5 py-4 bg-slate-50 border-t border-slate-200 flex items-center justify-between" aria-label="Queue pagination">
              <button
                onClick={() => setPage(Math.max(1, page - 1))}
                disabled={page === 1}
                className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
                aria-label="Go to previous page"
              >
                Previous
              </button>
              <span className="text-sm text-slate-600" aria-live="polite">
                Page {page} of {totalPages}
              </span>
              <button
                onClick={() => setPage(Math.min(totalPages, page + 1))}
                disabled={page === totalPages}
                className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
                aria-label="Go to next page"
              >
                Next
              </button>
            </nav>
          )}
            </div>
          </div>

          {/* Phase 3.2: Right Sidebar with Accountant Insights */}
          {showSidebar && (
            <div className="w-80 flex-shrink-0 space-y-4 max-h-[calc(100vh-200px)] overflow-y-auto sticky top-4">
              {selectedCaseId ? (
                <>
                  {/* Show case-specific insights when a case is selected */}
                  <TopPrioritiesCard reportId={selectedCaseId} maxItems={3} />
                  <StressTestSummary reportId={selectedCaseId} />
                  <DataHealthWidget reportId={selectedCaseId} />
                  <ForensicInsightsCard reportId={selectedCaseId} />
                  <NextActionsChecklist reportId={selectedCaseId} />
                  <PendingAISuggestionsWidget reportId={selectedCaseId} />
                  <InsuranceQuestionsCard reportId={selectedCaseId} />
                  <IndustryBenchmark
                    businessType={selectedCase?.business_type || undefined}
                    cogsRatio={caseMetrics?.cogsRatio}
                    grossMargin={caseMetrics?.grossMargin}
                    operatingExpenseRatio={caseMetrics?.operatingExpenseRatio}
                    netProfitMargin={caseMetrics?.netProfitMargin}
                  />
                </>
              ) : (
                <>
                  {/* Portfolio Overview when no case selected */}
                  <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-5" data-testid="portfolio-overview">
                    <h3 className="text-sm font-semibold text-slate-900 uppercase tracking-wide mb-4 flex items-center gap-2">
                      <BarChart3 className="w-4 h-4 text-blue-600" />
                      Pipeline Overview
                    </h3>
                    {overview?.pipeline && overview.pipeline.length > 0 ? (
                      <div className="space-y-3">
                        {overview.pipeline.map((p) => {
                          const maxCount = Math.max(...overview.pipeline.map(x => x.count));
                          const pct = maxCount > 0 ? (p.count / maxCount) * 100 : 0;
                          const stageLabels: Record<string, string> = {
                            data_specialist: 'Data Specialist',
                            accountant: 'Accountant',
                            ready: 'Ready',
                            ai_processing: 'AI Processing',
                            client: 'Client',
                            attorney: 'Attorney'
                          };
                          return (
                            <div key={p.stage} data-testid={`pipeline-${p.stage}`}>
                              <div className="flex items-center justify-between text-xs mb-1">
                                <span className="text-slate-600 font-medium">{stageLabels[p.stage] || p.stage}</span>
                                <span className="text-slate-900 font-semibold">{p.count}</span>
                              </div>
                              <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                                <div 
                                  className="h-full bg-blue-500 rounded-full transition-all duration-500"
                                  style={{ width: `${pct}%` }}
                                />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <p className="text-sm text-slate-400">No pipeline data yet</p>
                    )}
                  </div>

                  {/* Key Metrics */}
                  <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-5" data-testid="key-metrics">
                    <h3 className="text-sm font-semibold text-slate-900 uppercase tracking-wide mb-4 flex items-center gap-2">
                      <TrendingUp className="w-4 h-4 text-green-600" />
                      Key Metrics
                    </h3>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="bg-green-50 rounded-lg p-3 text-center" data-testid="metric-signoff">
                        <div className="text-lg font-bold text-green-700">{overview?.cases_ready_for_signoff ?? 0}</div>
                        <div className="text-xs text-green-600">Ready for Sign-off</div>
                      </div>
                      <div className="bg-amber-50 rounded-lg p-3 text-center" data-testid="metric-stale">
                        <div className="text-lg font-bold text-amber-700">{overview?.cases_stale_7_days ?? 0}</div>
                        <div className="text-xs text-amber-600">Stale (7+ days)</div>
                      </div>
                      <div className="bg-blue-50 rounded-lg p-3 text-center" data-testid="metric-strength">
                        <div className="text-lg font-bold text-blue-700">{overview?.avg_case_strength?.toFixed(0) ?? 0}%</div>
                        <div className="text-xs text-blue-600">Avg Strength</div>
                      </div>
                      <div className="bg-slate-50 rounded-lg p-3 text-center" data-testid="metric-pipeline-total">
                        <div className="text-lg font-bold text-slate-700">
                          {overview?.pipeline?.reduce((sum, p) => sum + p.count, 0) ?? 0}
                        </div>
                        <div className="text-xs text-slate-600">Total in Pipeline</div>
                      </div>
                    </div>
                  </div>

                  {/* Recent Activity Feed */}
                  <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-5" data-testid="recent-activity">
                    <h3 className="text-sm font-semibold text-slate-900 uppercase tracking-wide mb-4 flex items-center gap-2">
                      <Activity className="w-4 h-4 text-indigo-600" />
                      Recent Activity
                    </h3>
                    {overview?.recent_activity && overview.recent_activity.length > 0 ? (
                      <div className="space-y-3">
                        {overview.recent_activity.slice(0, 6).map((act, i) => {
                          const actionLabels: Record<string, string> = {
                            'attorney_ai_evaluation': 'AI Evaluation',
                            'case_update': 'Case Updated',
                            'case_claimed': 'Case Claimed',
                            'sign_off': 'Signed Off',
                            'annotation_created': 'Annotation',
                            'report_created': 'Report Created',
                            'draft_saved': 'Draft Saved',
                            'documents_uploaded': 'Documents',
                            'forensic_complete': 'Forensic',
                            'attorney_review': 'Attorney Review'
                          };
                          const actionColors: Record<string, string> = {
                            'attorney_ai_evaluation': 'bg-blue-400',
                            'case_update': 'bg-blue-400',
                            'forensic_complete': 'bg-green-400',
                            'attorney_review': 'bg-purple-400',
                            'documents_uploaded': 'bg-amber-400',
                            'case_claimed': 'bg-indigo-400',
                            'sign_off': 'bg-emerald-400'
                          };
                          const timeAgo = act.timestamp ? (() => {
                            const diff = Date.now() - new Date(act.timestamp).getTime();
                            const hours = Math.floor(diff / 3600000);
                            if (hours < 1) return 'Just now';
                            if (hours < 24) return `${hours}h ago`;
                            return `${Math.floor(hours / 24)}d ago`;
                          })() : '';
                          return (
                            <div key={i} className="flex items-start gap-3" data-testid={`activity-item-${i}`}>
                              <div className={`w-1.5 h-1.5 rounded-full mt-2 shrink-0 ${actionColors[act.action] || 'bg-indigo-400'}`} />
                              <div className="min-w-0 flex-1">
                                <p className="text-xs font-medium text-slate-700">
                                  {actionLabels[act.action] || act.action.replace(/_/g, ' ')}
                                </p>
                                {act.description && (
                                  <p className="text-xs text-slate-500 leading-snug">{act.description}</p>
                                )}
                                {act.business_name && (
                                  <p className="text-xs text-slate-400 truncate">{act.business_name}</p>
                                )}
                              </div>
                              <span className="text-xs text-slate-400 whitespace-nowrap shrink-0">{timeAgo}</span>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <p className="text-sm text-slate-400">No recent activity</p>
                    )}
                  </div>

                  {/* Select a case hint */}
                  <div className="bg-slate-50 rounded-xl border border-dashed border-slate-300 p-4 text-center" data-testid="select-case-hint">
                    <CheckCircle className="w-6 h-6 mx-auto text-slate-400 mb-1" />
                    <p className="text-xs text-slate-500">
                      Select a case to view forensic insights and next actions
                    </p>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
        </div>
      </div>
      </div>
    </AppLayout>
  );
};

export default AccountantQueue;
